import type { ProjectResponse } from '@/model/project/dto.generated';
import { useTrans } from '@/shared/hooks/useTranslation';
import { useProjectList } from '@/viewmodel/features/project/hooks/useProjectList';
import { toProjectSwitcherOption } from '@/viewmodel/features/project/mappers/projectSwitcherOption';
import type { ProjectSwitcher, ProjectSwitcherOption } from '@/viewmodel/features/project/types';
import { useState } from 'react';

type ProjectSwitcherArgs = {
  workspaceId: string | undefined;
  currentProjectId: string | undefined;
  onSelect: (project: ProjectResponse | undefined, value: string) => void;
  leadingOptions?: ProjectSwitcherOption[];
};

export function useProjectSwitcher({
  workspaceId,
  currentProjectId,
  onSelect,
  leadingOptions = [],
}: ProjectSwitcherArgs): ProjectSwitcher {
  const { t } = useTrans();
  const [query, setQuery] = useState('');
  const { projects, hasMore, loadMore, isFetching } = useProjectList(workspaceId, query);

  return {
    options: [...(query.trim() ? [] : leadingOptions), ...projects.map(toProjectSwitcherOption)],
    currentValue: currentProjectId ?? '',
    onSelect: (value) =>
      onSelect(
        projects.find((p) => p.id === value),
        value,
      ),
    filterPlaceholder: t('projects.filterPlaceholder'),
    onQueryChange: setQuery,
    hasMore,
    onLoadMore: loadMore,
    isLoading: isFetching,
  };
}
